import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import moment from "moment";
import { isEqual } from "lodash";
import React from "react";

import { createUseModal, useFormModal, ModalProps } from "@imminently/react/context/modals";
import {
  FormInputSelect,
  FormInputTel,
  FormInputText,
} from "@imminently/react/ui/forms";
import { CustomFormAutocomplete } from "./genericForm/fieldRegistry";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@imminently/react/ui/core/dialog";
import { Text } from "@imminently/react/ui/core/text";
import { Button, IconButton, Stack, Box, Divider } from "@mui/material";

import { Icon } from "@/components/Icon";
import { ActingAsBanner } from "@/components/ActingAsBanner";
import { ScrollIndicator } from "@/components/ScrollIndicator";
import { encounterService } from "@/services";
import { InfectiousSymptom, PatientEncounter } from "@/resources/types";
import { scrollable } from "@/components/scrollable";
import { formatName } from "@/global";
import { FormInputDate, FormInputTime, FormSymptomsSelect } from "./forms";
import { useTypes } from "@/providers/TypesProvider";
import DateOfBirthField from "./genericForm/DateOfBirthField";
import { cleanFormValues } from "./addPatient/addPatientContext";

const optionalValue = (value: any) => (value === "" ? undefined : value);
const optional = (schema: yup.AnySchema) =>
  yup.lazy((v) => (v === "" || v === null || v === undefined ? yup.mixed().notRequired() : schema));

const dateOfBirthSchema = () => yup.date()
  .transform((value, originalValue) => moment.isMoment(originalValue) ? originalValue.toDate() : value)
  .typeError("Enter a valid date of birth")
  .min(moment().subtract(120, "years").startOf("day").toDate(), "Date of birth cannot be more than 120 years ago")
  .max(moment().endOf("day").toDate(), "Date of birth cannot be in the future");

const createSchema = (genderOptions: any[], sexOptions: any[], mode: "create" | "edit" = "create") => {
  const baseSchema = yup.object().shape({
    firstName: yup.string().trim().required("First name is required"),
    lastName: yup.string().trim().required("Surname is required"),
    phone: optional(yup.string().phone()),
    email: optional(yup.string().email()),
    dob: optional(dateOfBirthSchema()),
    sex: yup.mixed().oneOf(["", ...sexOptions.map(opt => opt.value)]),
    gender: yup.mixed().oneOf(["", ...genderOptions.map(opt => opt.value)]),
    pronouns: yup.string().trim(),
    infectiousSymptoms: yup
      .array()
      .of(yup.mixed().oneOf(Object.values(InfectiousSymptom))),
    umrn: yup.string().trim(),
    arrivalDate: yup
      .date()
      .typeError("Invalid date")
      .required("Expected arrival date is required"),
    arrivalTime: yup
      .date()
      .typeError("Invalid time")
      .required("Expected arrival time is required"),
    admittingSpecialty: yup.string().trim(),
    bedSlipSubmitted: yup.mixed().oneOf(["", "yes", "no"]),
    triageDirection: yup.string().trim(),
    patientOrigin: yup.string().trim(),
    referrerFrom: yup.string().trim(),
    referrerContactName: yup.string().trim(),
    referrerContactNumber: optional(yup.string().trim().max(20, "Contact number must be 20 characters or less")),
    expects: yup.string().trim().required("Expects note is required"),
  });

  // Only validate future-time for create mode, not edit mode
  if (mode === "create") {
    return baseSchema.test(
      "future-time",
      "Expected arrival time must be in the future",
      (value, context) => {
        // Only validate if both date and time are provided
        if (!value.arrivalDate || !value.arrivalTime) {
          return true; // Skip validation if either field is missing
        }

        const inputDate = moment(value.arrivalDate);

        inputDate.set({
          hour: moment(value.arrivalTime).get("hour"),
          minute: moment(value.arrivalTime).get("minute"),
          second: 0,
        });

        // Allow times that are at least 1 minute in the future to account for timezone differences
        const now = moment().add(1, "minute");
        if (inputDate >= now) {
          return true;
        }

        return context.createError({
          message: "Arrival date and time must be in the future",
          path: "arrivalTime", // Show error on the time field since that's when user realizes the issue
        });
      }
    );
  }

  return baseSchema;
};

const initialState = {
  firstName: "",
  lastName: "",
  phone: "",
  email: "",
  dob: null,
  sex: "",
  gender: "",
  pronouns: "",
  infectiousSymptoms: [],
  umrn: "",
  arrivalDate: null,
  arrivalTime: null,
  admittingSpecialty: "",
  bedSlipSubmitted: "",
  triageDirection: "",
  patientOrigin: "",
  referrerFrom: "",
  referrerContactName: "",
  referrerContactNumber: "",
  expects: "",
};

const styles = {
  dialogContent: {
    pb: 3,
    pr: 2,
    scrollbarGutter: "stable",
  },
};

const { useMutateResource } = encounterService;

interface AddExpectsPatientModalProps {
  mode?: "create" | "edit";
  encounter?: PatientEncounter;
}

// eslint-disable-next-line complexity
const encounterToFormValues = (encounter: PatientEncounter) => {
  const patient = encounter.patient || {};
  const arrivalMoment = encounter.arrived ? moment(encounter.arrived) : null;
  const dob = patient.dateOfBirth ? moment(patient.dateOfBirth) : null;

  return {
    firstName: patient.firstName || "",
    lastName: patient.lastName || "",
    phone: patient.phone || "",
    email: patient.email || "",
    dob,
    sex: patient.sex || "",
    gender: patient.gender || "",
    pronouns: patient.pronouns || "",
    infectiousSymptoms: encounter.infectiousSymptoms || [],
    umrn: patient.umrn || "",
    arrivalDate: arrivalMoment,
    arrivalTime: arrivalMoment,
    admittingSpecialty: encounter.admittingSpecialty || "",
    bedSlipSubmitted: encounter.bedSlipSubmitted || "",
    triageDirection: encounter.triageDirection || "",
    patientOrigin: encounter.patientOrigin || "",
    referrerFrom: encounter.referrerDetails?.from || "",
    referrerContactName: encounter.referrerDetails?.contactName || "",
    referrerContactNumber: encounter.referrerDetails?.contactNumber || "",
    expects: encounter.problem?.expects || "",
  };
};

// Helper to filter options based on mode and current value
type OptionType = { value: string; label: string; disabled?: boolean };
const filterOptionsForMode = (
  options: OptionType[],
  mode: "create" | "edit",
  currentValue?: string
): OptionType[] => {
  if (mode === "create") {
    // Create mode: only active options
    return options.filter(opt => !opt.disabled);
  }
  // Edit mode: active options + current value if inactive
  const activeOptions = options.filter(opt => !opt.disabled);
  const currentOption = options.find(opt => opt.value === currentValue);
  if (currentOption && currentOption.disabled) {
    return [...activeOptions, currentOption];
  }
  return activeOptions;
};

const AddExpectsPatientModalContent: React.FC<AddExpectsPatientModalProps> = ({
  mode = "create",
  encounter,
}) => {
  const { genderOptions, sexOptions, pronounsOptions, triageDirectionOptions, patientOriginOptions } = useTypes();

  const { action: createAction, query: createQuery } = useMutateResource(undefined, {});
  const { mutateAsync: updateAction, isLoading: isUpdateLoading } = encounterService.useUpdate();

  const initialValues = React.useMemo(() => {
    if (encounter && mode === "edit") {
      return encounterToFormValues(encounter);
    }
    return initialState;
  }, [encounter, mode]);

  // Store original values for edit mode comparison
  const originalValues = React.useMemo(() => {
    if (encounter && mode === "edit") {
      return encounterToFormValues(encounter);
    }
    return null;
  }, [encounter, mode]);

  // Helper to compare values, handling special cases like moments
  const valuesAreEqual = (original: any, current: any): boolean => {
    // Handle moment objects
    if (moment.isMoment(original) && moment.isMoment(current)) {
      return original.valueOf() === current.valueOf();
    }
    if (moment.isMoment(original) || moment.isMoment(current)) {
      return false; // One is moment, other isn't
    }
    // Handle undefined vs empty string (they should be treated as equal for comparison)
    const origNormalized = original === "" ? undefined : original;
    const currNormalized = current === "" ? undefined : current;
    return isEqual(origNormalized, currNormalized);
  };

  // Filter payload to only include changed fields
  // For fields that had a value but now are empty, send "" to clear them
  const filterChangedFields = React.useCallback((
    payload: any,
    originalFormValues: any,
    currentFormData: any
  ): any => {
    if (!originalFormValues) return payload;

    const filtered: any = {};

    // Patient fields to check
    if (payload.patient) {
      const patientFiltered: any = {};
      const patientFields = ["firstName", "lastName", "phone", "email", "sex", "gender", "pronouns", "umrn"];
      
      patientFields.forEach(key => {
        const formKey = key === "dateOfBirth" ? "dob" : key;
        const originalVal = originalFormValues[formKey];
        const currentVal = currentFormData[formKey];
        
        if (!valuesAreEqual(originalVal, currentVal)) {
          if (key in payload.patient && payload.patient[key] !== undefined) {
            patientFiltered[key] = payload.patient[key];
          } else {
            // Field was cleared: original had value but current is empty
            const origNormalized = originalVal === "" ? undefined : originalVal;
            const currNormalized = currentVal === "" ? undefined : currentVal;
            if (origNormalized !== undefined && currNormalized === undefined) {
              patientFiltered[key] = "";
            }
          }
        }
      });

      // Handle dateOfBirth separately (moment comparison)
      const origDob = originalFormValues.dob;
      const currDob = currentFormData.dob;
      if (!valuesAreEqual(origDob, currDob)) {
        if (payload.patient.dateOfBirth !== undefined) {
          patientFiltered.dateOfBirth = payload.patient.dateOfBirth;
        } else if (origDob && !currDob) {
          // DOB was cleared
          patientFiltered.dateOfBirth = "";
        }
      }

      if (Object.keys(patientFiltered).length > 0) {
        filtered.patient = patientFiltered;
      }
    }

    // Encounter fields to check
    const encounterFields = [
      "arrived", "admittingSpecialty", "bedSlipSubmitted", 
      "triageDirection", "patientOrigin"
    ];

    encounterFields.forEach(key => {
      const formKey = key === "arrived" ? "arrivalDate" : key; // arrived is computed from arrivalDate/arrivalTime
      let originalVal: any;
      let currentVal: any;

      if (key === "arrived") {
        // Compare computed arrival timestamps
        const origDate = originalFormValues.arrivalDate;
        const origTime = originalFormValues.arrivalTime;
        const currDate = currentFormData.arrivalDate;
        const currTime = currentFormData.arrivalTime;
        
        // If either date/time changed, include arrived
        if (!valuesAreEqual(origDate, currDate) || !valuesAreEqual(origTime, currTime)) {
          if (payload[key] !== undefined) {
            filtered[key] = payload[key];
          }
        }
        return;
      }

      originalVal = originalFormValues[formKey];
      currentVal = currentFormData[formKey];

      if (!valuesAreEqual(originalVal, currentVal)) {
        if (key in payload && payload[key] !== undefined) {
          filtered[key] = payload[key];
        } else {
          // Field was cleared
          const origNormalized = originalVal === "" ? undefined : originalVal;
          const currNormalized = currentVal === "" ? undefined : currentVal;
          if (origNormalized !== undefined && currNormalized === undefined) {
            filtered[key] = "";
          }
        }
      }
    });

    // Handle infectiousSymptoms array
    const origSymptoms = originalFormValues.infectiousSymptoms || [];
    const currSymptoms = currentFormData.infectiousSymptoms || [];
    if (!isEqual(origSymptoms, currSymptoms)) {
      // If the field exists in payload, use it; otherwise send empty array to clear it
      // This handles the case where an array was changed to empty (removed from payload)
      if (payload.infectiousSymptoms !== undefined) {
        filtered.infectiousSymptoms = payload.infectiousSymptoms;
      } else {
        // Array was cleared: original had values but current is empty - send empty array to clear it
        filtered.infectiousSymptoms = [];
      }
    }

    // Handle problem.expects
    const origExpects = originalFormValues.expects || "";
    const currExpects = currentFormData.expects || "";
    if (origExpects !== currExpects) {
      filtered.problem = payload.problem;
    }

    // Handle referrerDetails - compare each sub-field
    const referrerFields = ["referrerFrom", "referrerContactName", "referrerContactNumber"];
    const referrerChanged = referrerFields.some(field => {
      const origVal = originalFormValues[field] || "";
      const currVal = currentFormData[field] || "";
      return origVal !== currVal;
    });

    if (referrerChanged) {
      // Build referrerDetails with only changed fields, sending "" for cleared fields
      const referrerDetails: any = {};
      referrerFields.forEach(field => {
        const backendKey = field === "referrerFrom" ? "from" : 
                          field === "referrerContactName" ? "contactName" : "contactNumber";
        const origVal = originalFormValues[field] || "";
        const currVal = currentFormData[field] || "";
        if (origVal !== currVal) {
          // Send the new value, or "" if cleared
          referrerDetails[backendKey] = currVal || "";
        }
      });
      if (Object.keys(referrerDetails).length > 0) {
        filtered.referrerDetails = referrerDetails;
      }
    }

    return filtered;
  }, []);

  const { methods, notify, close } = useFormModal({
    mode: "all",
    defaultValues: initialValues,
    // @ts-ignore go away
    resolver: yupResolver(createSchema(genderOptions, sexOptions, mode)),
  });


  const {
    control,
    formState: { isValid },
    handleSubmit: handleFormSubmit,
    watch,
    getValues,
  } = methods;

  const infectiousSymptoms = watch("infectiousSymptoms") as InfectiousSymptom[];

  // Watch current values for filtering
  const currentSex = watch("sex");
  const currentGender = watch("gender");
  const currentTriageDirection = watch("triageDirection");
  const currentPatientOrigin = watch("patientOrigin");

  // Filter options based on mode and current values
  const filteredSexOptions = React.useMemo(
    () => filterOptionsForMode(sexOptions, mode, currentSex),
    [sexOptions, mode, currentSex]
  );
  const filteredGenderOptions = React.useMemo(
    () => filterOptionsForMode(genderOptions, mode, currentGender),
    [genderOptions, mode, currentGender]
  );
  const filteredTriageDirectionOptions = React.useMemo(
    () => filterOptionsForMode(triageDirectionOptions, mode, currentTriageDirection),
    [triageDirectionOptions, mode, currentTriageDirection]
  );
  const filteredPatientOriginOptions = React.useMemo(
    () => filterOptionsForMode(patientOriginOptions, mode, currentPatientOrigin),
    [patientOriginOptions, mode, currentPatientOrigin]
  );

  const buildPayload = (rawData: any) => {
    // Clean all string values to remove leading/trailing whitespace
    const data = cleanFormValues(rawData);

    const arrivalDateMoment = moment(data.arrivalDate);
    const arrivalTimeMoment = moment(data.arrivalTime, "HH:mm");

    arrivalDateMoment.set({
      hour: arrivalTimeMoment.get("hour"),
      minute: arrivalTimeMoment.get("minute"),
      second: 0,
    });

    const patient: any = {
      firstName: data.firstName,
      lastName: data.lastName,
      phone: optionalValue(data.phone),
      email: optionalValue(data.email),
      sex: optionalValue(data.sex),
      gender: optionalValue(data.gender),
      pronouns: optionalValue(data.pronouns),
      dateOfBirth: data.dob && moment(data.dob).isValid()
        ? moment(data.dob).format("YYYY-MM-DD")
        : undefined,
      medicareNo: optionalValue(data.medicareNumber),
      umrn: optionalValue(data.umrn),
    };

    const hasSymptoms = data.infectiousSymptoms.length > 0;

    // Build referrer details if any field is filled
    const referrerDetails = (data.referrerFrom || data.referrerContactName || data.referrerContactNumber) ? {
      from: optionalValue(data.referrerFrom),
      contactName: optionalValue(data.referrerContactName),
      contactNumber: optionalValue(data.referrerContactNumber),
    } : undefined;

    return {
      status: "expected" as const,
      infectiousSymptoms: hasSymptoms ? data.infectiousSymptoms : undefined,
      arrived: arrivalDateMoment.valueOf(),
      problem: { expects: data.expects },
      patient,
      admittingSpecialty: optionalValue(data.admittingSpecialty),
      bedSlipSubmitted: optionalValue(data.bedSlipSubmitted),
      triageDirection: optionalValue(data.triageDirection),
      patientOrigin: optionalValue(data.patientOrigin),
      referrerDetails,
    };
  };

  const handleSubmit = (data: any) => {
    const payload = buildPayload(data);
    const name = formatName(data.firstName, data.lastName);

    if (mode === "edit" && encounter) {
      // Filter to only include changed fields
      const filteredPayload = filterChangedFields(payload, originalValues, data);

      // Only send update if there are actual changes
      if (Object.keys(filteredPayload).length === 0) {
        notify.success("No changes to save");
        return;
      }

      updateAction({ id: encounter.id, payload: filteredPayload })
        .then(() => {
          notify.success(`Updated patient: ${name}`);
          close(true);
        })
        .catch(() => notify.error("Failed to update patient"));
    } else {
      createAction(payload as any)
        .then(() => {
          notify.success(`Created patient: ${name}`);
          close(true);
        })
        .catch(() => notify.error("Failed to create patient"));
    }
  };

  const handleClose = () => close();

  const isLoading = mode === "edit" ? isUpdateLoading : createQuery.isLoading;

  return (
    <Dialog open onClose={handleClose} maxWidth="lg" sx={{ "& .MuiDialog-paper": { position: "relative", width: "1040px", maxWidth: "1040px" } }}>
      <ActingAsBanner />
      <DialogTitle>
        <Text variant="h3">{mode === "edit" ? "Edit expects" : "Create expects"}</Text>
        <IconButton onClick={handleClose} tabIndex={-1}>
          <Icon icon="close" />
        </IconButton>
      </DialogTitle>

      <DialogContent className={scrollable} sx={styles.dialogContent}>
        <Text mb={3}>
          Non-required fields can be modified after creation in the patient
          detail screen
        </Text>
        <Stack gap={2}>
          {/* Top row: Arrival date/time and Name */}
          <Stack direction="row" gap={2}>
            <FormInputDate
              required
              disablePast
              name="arrivalDate"
              label="Expected arrival date"
              format="DD/MM/YYYY"
              control={control}
              sx={{ flex: 1 }}
              slotProps={{
                textField: {
                  autoFocus: true,
                },
              }}
            />
            <FormInputTime
              required
              name="arrivalTime"
              label="Expected arrival time"
              control={control}
              sx={{ flex: 1 }}
            />
            <FormInputText
              required
              sx={{ flex: 1 }}
              name="lastName"
              label="Surname"
              control={control}
            />
            <FormInputText
              required
              sx={{ flex: 1 }}
              name="firstName"
              label="First name"
              control={control}
            />
          </Stack>

          <Divider />

          {/* Two columns layout */}
          <Stack direction="row" gap={2} alignItems="flex-start">
            {/* Column A: expects note, phone, email, DOB, sex, gender, pronouns */}
            <Stack gap={2} sx={{ flex: 1 }}>
              <FormInputText
                required
                name="expects"
                label="Expects note"
                control={control}
                multiline
                minRows={4}
              />
              <FormInputTel name="phone" label="Phone" control={control} />
              {/* <FormInputText name="email" label="Email" control={control} /> lightly remove this, as it may be requested later */}
              <FormInputText
                name="umrn"
                label="UMRN"
                control={control}
                helperText="Hospital Unit Medical Record Number"
              />
              <Stack direction="row" gap={1}>
                <DateOfBirthField
                  name="dob"
                  label="Date of birth"
                  format="DD/MM/YYYY"
                  control={control}
                  getValues={getValues}
                  sx={{ flex: 1 }}
                />
                <CustomFormAutocomplete
                  sx={{ flex: 1 }}
                  name="sex"
                  label="Sex at birth"
                  control={control}
                  options={filteredSexOptions}
                />

              </Stack>
              <Stack direction="row" gap={1}>
                <CustomFormAutocomplete
                  sx={{ flex: 1 }}
                  name="gender"
                  label="Gender"
                  control={control}
                  options={filteredGenderOptions}
                />
                <CustomFormAutocomplete
                  sx={{ flex: 1 }}
                  name="pronouns"
                  label="Pronouns"
                  control={control}
                  options={pronounsOptions}
                  helperText="Optional"
                />
              </Stack>
            </Stack>

            {/* Column B: infectious, UMRN, specialty, bed slip, triage, patient origin, referrer */}
            <Stack gap={2} sx={{ flex: 1 }}>
              <FormSymptomsSelect
                name="infectiousSymptoms"
                control={control}
                selectedSymptoms={infectiousSymptoms}
              />
              <FormInputText
                name="admittingSpecialty"
                label="Admitting specialty"
                control={control}
              />
              <CustomFormAutocomplete
                name="bedSlipSubmitted"
                label="Bed slip submitted / Bed manager aware"
                control={control}
                options={[
                  { value: "yes", label: "Yes" },
                  { value: "no", label: "No" },
                ]}
              />
              <CustomFormAutocomplete
                name="triageDirection"
                label="Triage directions"
                control={control}
                options={filteredTriageDirectionOptions}
              />
              <CustomFormAutocomplete
                name="patientOrigin"
                label="Patient origin"
                control={control}
                options={filteredPatientOriginOptions}
              />
              <Box
                component="fieldset"
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1,
                  padding: 2,
                  margin: 0,
                  "& legend": {
                    padding: "0 8px",
                    fontSize: "0.875rem",
                    fontWeight: 600,
                    color: "text.primary",
                  },
                }}
              >
                <Box component="legend">Referrer details</Box>
                <Stack gap={2} mt={1}>
                  <Stack direction="row" gap={1}>
                    <FormInputText
                      sx={{ flex: 1 }}
                      name="referrerFrom"
                      label="From"
                      control={control}
                    />
                    <FormInputText
                      sx={{ flex: 1 }}
                      name="referrerContactName"
                      label="Contact name"
                      control={control}
                    />
                  </Stack>
                  <FormInputText
                    name="referrerContactNumber"
                    label="Contact number"
                    control={control}
                  />
                </Stack>
              </Box>
            </Stack>
          </Stack>
        </Stack>
      </DialogContent>

      <DialogActions
        sx={{
          position: "sticky",
          bottom: 0,
          backgroundColor: "background.paper",
          zIndex: 1,
          borderTop: "1px solid",
          borderColor: "divider",
        }}
      >
        <Button
          disabled={!isValid || isLoading}
          onClick={handleFormSubmit(handleSubmit)}
        >
          {mode === "edit" ? "Update" : "Create"}
        </Button>
      </DialogActions>

      <ScrollIndicator />
    </Dialog>
  );
};

// Modal wrapper component
export const AddExpectsPatientModal: React.FC<ModalProps<AddExpectsPatientModalProps>> = ({
  props: modalProps
}) => {
  return <AddExpectsPatientModalContent {...modalProps} />;
};

export const useAddExpectsPatientModal = createUseModal(AddExpectsPatientModal);
